import { OrbitControls } from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as THREE from 'three';
import { createBox3D, TransformBuffer, type Box3D, type Vec3 } from 'box3d-web';
import { useViewportWidth } from '../hooks/useViewportWidth';
import evaluationReport from '../../artifacts/sports-ball-evaluation.json';
import {
  BALL_SURFACES,
  SPORTS_BALL_PRESETS,
  SportsBallSimulation,
  createSportsBall,
  launchSportsBall,
  resolveSportsBallSpec,
  type BallForceSample,
  type BallSurfaceId,
  type SportsBallId,
  type SportsBallRuntime,
  type SportsBallSpec,
} from '../physics/sportsBall';
import {
  createSportsBallProfile,
  serializeSportsBallProfile,
  type SportsBallProfile,
} from '../physics/sportsBallProfile';
import './BallLabV2.css';

type ScenarioId = 'drop' | 'launch' | 'roll' | 'topspin' | 'backspin' | 'wall-impact';

interface LaneConfig {
  surfaceId: BallSurfaceId;
  massKg: number;
  restitutionScale: number;
  dragScale: number;
  liftScale: number;
}

interface LaneMetrics {
  ready: boolean;
  heightM: number;
  speedMps: number;
  spinRpm: number;
  reynolds: number;
  dragCoefficient: number;
  liftCoefficient: number;
  reboundApexM: number | null;
  slipMps: number;
  contactSeen: boolean;
  exitSpeedMps: number | null;
}

interface BallLabState {
  ready: boolean;
  preset: SportsBallId;
  activePreset: SportsBallId;
  scenario: ScenarioId;
  speedMps: number;
  reboundApexM: number | null;
  certification: 'pending' | 'pass' | 'fail';
  profileMode: 'reference' | 'game';
  profileJson: string;
  metricsByPreset: Record<SportsBallId, LaneMetrics>;
  profilesByPreset: Record<SportsBallId, SportsBallProfile>;
  reportVersion: number;
  suitePass: boolean;
  calibrationStatus: 'empirically-calibrated' | 'partially-calibrated';
}

declare global {
  interface Window {
    __ballLabState?: BallLabState;
  }
}

const IDS: readonly SportsBallId[] = ['football', 'basketball', 'tennis'];
const LANE_Z: Readonly<Record<SportsBallId, number>> = { football: -2.8, basketball: 0, tennis: 2.8 };
const modulePromise: Promise<Box3D> = createBox3D();

const SCENARIOS: ReadonlyArray<{ id: ScenarioId; label: string; blurb: string }> = [
  { id: 'drop', label: 'Drop', blurb: 'Regulation rebound' },
  { id: 'launch', label: 'Launch', blurb: 'Drag retention' },
  { id: 'roll', label: 'Roll', blurb: 'Surface resistance' },
  { id: 'topspin', label: 'Topspin', blurb: 'Forward exit' },
  { id: 'backspin', label: 'Backspin', blurb: 'Braking exit' },
  { id: 'wall-impact', label: 'Wall', blurb: 'CCD / no tunnel' },
];

const EMPTY_METRIC: LaneMetrics = {
  ready: false,
  heightM: 0,
  speedMps: 0,
  spinRpm: 0,
  reynolds: 0,
  dragCoefficient: 0,
  liftCoefficient: 0,
  reboundApexM: null,
  slipMps: 0,
  contactSeen: false,
  exitSpeedMps: null,
};

export default function BallLabV2({ onBack }: { onBack: () => void }) {
  const [activePreset, setActivePreset] = useState<SportsBallId>('football');
  const [scenario, setScenario] = useState<ScenarioId>('drop');
  const [configs, setConfigs] = useState<Record<SportsBallId, LaneConfig>>(() => ({
    football: defaultConfig('football'),
    basketball: defaultConfig('basketball'),
    tennis: defaultConfig('tennis'),
  }));
  const [metrics, setMetrics] = useState<Record<SportsBallId, LaneMetrics>>(() => emptyMetrics());
  const [runId, setRunId] = useState(0);
  const [controlsOpen, setControlsOpen] = useState(false);
  const viewportWidth = useViewportWidth();
  const isNarrowCanvas = viewportWidth <= 640;
  const isMobile = viewportWidth <= 900;

  const specs = useMemo(() => Object.fromEntries(IDS.map((id) => [id, resolveSpec(id, configs[id])])) as Record<SportsBallId, SportsBallSpec>, [configs]);
  const profiles = useMemo(() => Object.fromEntries(IDS.map((id) => [id, buildProfile(id, configs[id])])) as Record<SportsBallId, SportsBallProfile>, [configs]);
  const active = configs[activePreset];
  const activeBase = SPORTS_BALL_PRESETS[activePreset];
  const activeProfile = profiles[activePreset];
  const suitePass = evaluationReport.engineeringPass;
  const calibrationStatus: BallLabState['calibrationStatus'] =
    evaluationReport.calibrationStatus === 'empirically-calibrated'
      ? 'empirically-calibrated'
      : 'partially-calibrated';

  useEffect(() => {
    const activeMetrics = metrics[activePreset];
    window.__ballLabState = {
      ready: IDS.every((id) => metrics[id].ready),
      preset: activePreset,
      activePreset,
      scenario,
      speedMps: activeMetrics.speedMps,
      reboundApexM: activeMetrics.reboundApexM,
      certification: activeProfile.mode === 'reference' ? suitePass ? 'pass' : 'fail' : 'pending',
      profileMode: activeProfile.mode,
      profileJson: serializeSportsBallProfile(activeProfile),
      metricsByPreset: metrics,
      profilesByPreset: profiles,
      reportVersion: evaluationReport.reportVersion,
      suitePass,
      calibrationStatus,
    };
  }, [activePreset, activeProfile, calibrationStatus, metrics, profiles, scenario, suitePass]);

  const updateActive = (patch: Partial<LaneConfig>) => {
    setConfigs((current) => ({
      ...current,
      [activePreset]: { ...current[activePreset], ...patch },
    }));
    setRunId((value) => value + 1);
  };

  const resetActive = () => {
    setConfigs((current) => ({ ...current, [activePreset]: defaultConfig(activePreset) }));
    setRunId((value) => value + 1);
  };

  const chooseScenario = (next: ScenarioId) => {
    setScenario(next);
    setRunId((value) => value + 1);
  };

  const presetChips = (
    <div className="ball-v2-segmented">
      {IDS.map((id) => (
        <button
          type="button"
          key={id}
          data-testid={`ball-preset-${id}`}
          className={activePreset === id ? 'active' : ''}
          onClick={() => setActivePreset(id)}
        >
          {shortLabel(id)}
        </button>
      ))}
    </div>
  );

  const modeBadge = (
    <div className={`ball-v2-mode ${activeProfile.mode}`} data-testid="ball-profile-mode">
      {activeProfile.mode === 'reference' ? 'REFERENCE PROFILE' : 'GAME OVERRIDE / NOT CERTIFIED'}
    </div>
  );

  const scenarioChips = (
    <div className="ball-v2-scenarios">
      {SCENARIOS.map((item) => (
        <button
          type="button"
          key={item.id}
          data-testid={`ball-scenario-${item.id}`}
          className={scenario === item.id ? 'active' : ''}
          onClick={() => chooseScenario(item.id)}
        >
          <strong>{item.label}</strong>
          <span>{item.blurb}</span>
        </button>
      ))}
    </div>
  );

  const surfaceSection = (
    <ControlSection title="Active lane surface">
      <select data-testid="ball-surface" value={active.surfaceId} onChange={(event) => updateActive({ surfaceId: event.target.value as BallSurfaceId })}>
        {(Object.keys(BALL_SURFACES) as BallSurfaceId[]).map((id) => <option key={id} value={id}>{BALL_SURFACES[id].label}</option>)}
      </select>
    </ControlSection>
  );

  const overridesSection = (
    <ControlSection title="Explicit overrides">
      <Slider testId="override-mass" label="Mass" value={active.massKg} min={activeBase.massRangeKg[0]} max={activeBase.massRangeKg[1]} step={0.001} suffix=" kg" onChange={(value) => updateActive({ massKg: value })} />
      <Slider testId="override-bounce" label="Bounce" value={active.restitutionScale} min={0.82} max={1.12} step={0.01} suffix="x" onChange={(value) => updateActive({ restitutionScale: value })} />
      <Slider testId="override-drag" label="Drag" value={active.dragScale} min={0} max={1.8} step={0.05} suffix="x" onChange={(value) => updateActive({ dragScale: value })} />
      <Slider testId="override-magnus" label="Magnus" value={active.liftScale} min={0} max={1.8} step={0.05} suffix="x" onChange={(value) => updateActive({ liftScale: value })} />
    </ControlSection>
  );

  const actionButtons = (
    <div className="ball-v2-actions">
      <button type="button" data-testid="ball-run" onClick={() => setRunId((value) => value + 1)}>Run all</button>
      <button type="button" data-testid="ball-reset-reference" onClick={resetActive}>Reset active</button>
    </div>
  );

  return (
    <div className="ball-v2-page">
      <header className="ball-v2-header">
        <button type="button" className="ball-v2-back" onClick={onBack}>Back</button>
        <div className="ball-v2-title">
          <span>BOX3D SPORTS LAB / REPORT v{evaluationReport.reportVersion}</span>
          <h1>Three balls. One tunable physics system.</h1>
        </div>
        <div className={`ball-v2-suite ${suitePass ? 'pass' : 'fail'}`} data-testid="ball-certification">
          <span>ENGINEERING {suitePass ? 'PASS' : 'FAIL'}</span>
          <small>LIFT CAL {calibrationStatus === 'empirically-calibrated' ? 'MEASURED' : 'PARTIAL'}</small>
        </div>
      </header>

      <main className="ball-v2-main">
        {!isMobile && (
          <aside className="ball-v2-controls">
            <div className="ball-v2-quickbar">
              <ControlSection title="Edit lane">
                {presetChips}
                {modeBadge}
              </ControlSection>
              <ControlSection title="Scenario protocol">
                {scenarioChips}
              </ControlSection>
            </div>

            <div className="ball-v2-drawer">
              {surfaceSection}
              {overridesSection}
              {actionButtons}
            </div>
          </aside>
        )}

        <section className="ball-v2-workspace">
          <div className="ball-v2-stage">
            <Canvas
              shadows={{ type: THREE.PCFShadowMap }}
              camera={isNarrowCanvas
                ? { position: [10.5, 7.4, 13.1], fov: 54, near: 0.01, far: 120 }
                : { position: [7.7, 5.2, 9.6], fov: 42, near: 0.01, far: 90 }}
              onCreated={({ gl }) => gl.setClearColor('#061015')}>
              <Suspense fallback={null}>
                <ComparisonScene key={`${scenario}-${runId}`} specs={specs} configs={configs} scenario={scenario} onMetrics={setMetrics} />
              </Suspense>
            </Canvas>
            <div className="ball-v2-stage-vignette" />
            <div className="ball-v2-lane-labels">
              {IDS.map((id) => (
                <div key={id} className={activePreset === id ? 'active' : ''}>
                  <span>{shortLabel(id)}</span>
                  <strong>{profiles[id].mode === 'reference' ? 'MATCHED REFERENCE' : 'LIVE + REFERENCE GHOST'}</strong>
                </div>
              ))}
            </div>
            {isMobile && (
              <div className="ball-v2-mobile-stats" data-testid="ball-mobile-stats">
                <span>{shortLabel(activePreset)} / {scenario}</span>
                <strong>{scenarioOutcome(scenario, metrics[activePreset])}</strong>
              </div>
            )}
          </div>

          {!isMobile && <ComparisonTable metrics={metrics} profiles={profiles} activePreset={activePreset} scenario={scenario} />}
        </section>
      </main>

      {isMobile && (
        <div className="ball-v2-dock">
          <div className="ball-v2-dock-chips">
            {presetChips}
            {scenarioChips}
          </div>
          <button
            type="button"
            className="ball-v2-dock-toggle"
            data-testid="lab-controls-toggle"
            aria-expanded={controlsOpen}
            onClick={() => setControlsOpen((value) => !value)}
          >
            Controls
          </button>
        </div>
      )}

      {isMobile && (
        <>
          <div
            className={`ball-v2-sheet-backdrop ${controlsOpen ? 'open' : ''}`}
            onClick={() => setControlsOpen(false)}
          />
          <div className={`ball-v2-sheet ${controlsOpen ? 'open' : ''}`}>
            <div className="ball-v2-sheet-handle-row">
              <span className="ball-v2-sheet-handle" />
              <button type="button" className="ball-v2-sheet-close" onClick={() => setControlsOpen(false)} aria-label="Close controls">
                Close
              </button>
            </div>
            <div className="ball-v2-sheet-body">
              {modeBadge}
              {surfaceSection}
              {overridesSection}
              {actionButtons}
              <div className="ball-v2-table-wrap">
                <ComparisonTable metrics={metrics} profiles={profiles} activePreset={activePreset} scenario={scenario} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ComparisonScene({ specs, configs, scenario, onMetrics }: {
  specs: Record<SportsBallId, SportsBallSpec>;
  configs: Record<SportsBallId, LaneConfig>;
  scenario: ScenarioId;
  onMetrics: (metrics: Record<SportsBallId, LaneMetrics>) => void;
}) {
  const runtimeRef = useRef<ComparisonPair | null>(null);
  const liveMeshes = useRef<Record<SportsBallId, THREE.Mesh | null>>({ football: null, basketball: null, tennis: null });
  const ghostMeshes = useRef<Record<SportsBallId, THREE.Mesh | null>>({ football: null, basketball: null, tennis: null });
  const liveTrails = useMemo(() => createTrails(false), []);
  const ghostTrails = useMemo(() => createTrails(true), []);
  const motion = useRef<Record<SportsBallId, MotionState>>(emptyMotion());
  const telemetryElapsed = useRef(0);

  useEffect(() => {
    let cancelled = false;
    createComparisonPair(specs, configs, scenario).then((pair) => {
      if (cancelled) {
        destroyPair(pair);
        return;
      }
      runtimeRef.current = pair;
      motion.current = emptyMotion();
      onMetrics(makeMetrics(pair.live, specs, motion.current));
    });
    return () => {
      cancelled = true;
      if (runtimeRef.current) destroyPair(runtimeRef.current);
      runtimeRef.current = null;
    };
  }, [configs, onMetrics, scenario, specs]);

  useFrame((_, delta) => {
    const pair = runtimeRef.current;
    if (!pair) return;
    const stepOptions = scenario === 'wall-impact' ? { impactNormal: [-1, 0, 0] as Vec3 } : {};
    pair.live.simulation.advance(Math.min(delta, 0.1), stepOptions, () => {
      pair.live.buffer.readInto(pair.live.world);
      updateMotion(pair.live, specs, motion.current);
    });
    pair.reference.simulation.advance(Math.min(delta, 0.1), stepOptions);
    pair.live.buffer.readInto(pair.live.world);
    pair.reference.buffer.readInto(pair.reference.world);

    IDS.forEach((id, index) => {
      updateMesh(liveMeshes.current[id], pair.live.transforms, index);
      updateMesh(ghostMeshes.current[id], pair.reference.transforms, index);
      const liveMesh = liveMeshes.current[id];
      const ghostMesh = ghostMeshes.current[id];
      if (liveMesh) pushTrail(liveTrails[id], liveMesh.position);
      if (ghostMesh) pushTrail(ghostTrails[id], ghostMesh.position);
    });

    telemetryElapsed.current += delta;
    if (telemetryElapsed.current >= 0.08) {
      telemetryElapsed.current = 0;
      onMetrics(makeMetrics(pair.live, specs, motion.current));
    }
  });

  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[4, 9, 5]} intensity={3.1} castShadow shadow-mapSize={[2048, 2048]} />
      <pointLight position={[-4, 3, -3]} intensity={22} color="#79eedb" distance={12} />
      {IDS.map((id) => (
        <group key={id}>
          <primitive object={ghostTrails[id].line} />
          <primitive object={liveTrails[id].line} />
          <mesh ref={(mesh) => { ghostMeshes.current[id] = mesh; }} scale={2.45} renderOrder={1}>
            <sphereGeometry args={[SPORTS_BALL_PRESETS[id].radiusM, 18, 12]} />
            <meshBasicMaterial color="#79eedb" wireframe transparent opacity={0.28} depthWrite={false} />
          </mesh>
          <mesh ref={(mesh) => { liveMeshes.current[id] = mesh; }} castShadow receiveShadow renderOrder={2}>
            <sphereGeometry args={[specs[id].radiusM, 36, 24]} />
            <meshStandardMaterial color={specs[id].visual.color} roughness={0.42} emissive={specs[id].visual.color} emissiveIntensity={0.1} />
            <mesh scale={1.8}>
              <sphereGeometry args={[specs[id].radiusM, 16, 10]} />
              <meshBasicMaterial color={specs[id].visual.color} wireframe transparent opacity={0.12} depthWrite={false} />
            </mesh>
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, LANE_Z[id]]} receiveShadow>
            <planeGeometry args={[18, 2.5]} />
            <meshStandardMaterial color={BALL_SURFACES[configs[id].surfaceId].color} roughness={0.94} />
          </mesh>
          <gridHelper args={[18, 36, '#58cdbd', '#173a42']} position={[0, 0.003, LANE_Z[id]]} scale={[1, 1, 0.14]} />
          {scenario === 'wall-impact' && (
            <mesh position={[0, 1, LANE_Z[id]]} castShadow receiveShadow>
              <boxGeometry args={[0.04, 2, 2.4]} />
              <meshStandardMaterial color="#f5ce69" emissive="#6f551a" emissiveIntensity={0.2} />
            </mesh>
          )}
        </group>
      ))}
      <OrbitControls makeDefault enablePan={false} minDistance={6} maxDistance={22} target={[0, 1, 0]} />
    </>
  );
}

function ComparisonTable({ metrics, profiles, activePreset, scenario }: {
  metrics: Record<SportsBallId, LaneMetrics>;
  profiles: Record<SportsBallId, SportsBallProfile>;
  activePreset: SportsBallId;
  scenario: ScenarioId;
}) {
  const rows: Array<{ label: string; value: (id: SportsBallId) => string }> = [
    { label: 'Height', value: (id) => `${metrics[id].heightM.toFixed(2)} m` },
    { label: 'Speed', value: (id) => `${metrics[id].speedMps.toFixed(1)} m/s` },
    { label: 'Spin', value: (id) => `${Math.round(metrics[id].spinRpm)} rpm` },
    { label: 'Re / Cd / Cl', value: (id) => `${formatCompact(metrics[id].reynolds)} / ${metrics[id].dragCoefficient.toFixed(2)} / ${metrics[id].liftCoefficient.toFixed(2)}` },
    { label: 'Outcome', value: (id) => scenarioOutcome(scenario, metrics[id]) },
  ];
  return (
    <div className="ball-v2-table" data-testid="ball-comparison-table">
      <div className="ball-v2-table-head"><span>LIVE + ARTIFACT GATES</span>{IDS.map((id) => (
        <div key={id} className={activePreset === id ? 'active' : ''}>
          <span className="ball-v2-column-title"><strong>{shortLabel(id)}</strong><em className={profiles[id].mode}>{profiles[id].mode === 'reference' ? 'REF PASS' : 'OVERRIDE'}</em></span>
          <span className="ball-v2-gates">
            {([['DROP', 'rebound'], ['DRAG', 'dragGolden'], ['MAG', 'magnusSymmetry'], ['SURF', 'surfaceCoupling'], ['DET', 'determinism'], ['CAD', 'timestepConvergence'], ['CCD', 'ccd'], ['GAME', 'customization']] as const).map(([label, gate]) => (
              <i key={gate} className={evaluationReport.scenarioGates[id][gate] ? 'pass' : 'fail'}>{label}</i>
            ))}
          </span>
        </div>
      ))}</div>
      {rows.map((row) => <div className="ball-v2-table-row" key={row.label}><span>{row.label}</span>{IDS.map((id) => <strong key={id} className={activePreset === id ? 'active' : ''}>{row.value(id)}</strong>)}</div>)}
    </div>
  );
}

interface SimulationBundle {
  world: ReturnType<Box3D['createWorld']>;
  simulation: SportsBallSimulation;
  balls: Record<SportsBallId, SportsBallRuntime>;
  buffer: TransformBuffer;
  transforms: Float32Array;
}

interface ComparisonPair { live: SimulationBundle; reference: SimulationBundle }
interface MotionState { previousY: number; rising: boolean; apex: number | null; contactSeen: boolean; exitSpeed: number | null }

async function createComparisonPair(specs: Record<SportsBallId, SportsBallSpec>, configs: Record<SportsBallId, LaneConfig>, scenario: ScenarioId): Promise<ComparisonPair> {
  const box3d = await modulePromise;
  const referenceSpecs = Object.fromEntries(IDS.map((id) => [id, resolveSportsBallSpec(id)])) as Record<SportsBallId, SportsBallSpec>;
  const referenceConfigs = Object.fromEntries(IDS.map((id) => [id, defaultConfig(id)])) as Record<SportsBallId, LaneConfig>;
  return {
    live: createSimulationBundle(box3d, specs, configs, scenario),
    reference: createSimulationBundle(box3d, referenceSpecs, referenceConfigs, scenario),
  };
}

function createSimulationBundle(box3d: Box3D, specs: Record<SportsBallId, SportsBallSpec>, configs: Record<SportsBallId, LaneConfig>, scenario: ScenarioId): SimulationBundle {
  const world = box3d.createWorld({ gravity: [0, -9.80665, 0], enableSleep: false, enableContinuous: true });
  const balls = {} as Record<SportsBallId, SportsBallRuntime>;
  const buffer = new TransformBuffer(3);
  for (const id of IDS) {
    const surface = BALL_SURFACES[configs[id].surfaceId];
    const lane = LANE_Z[id];
    const floor = world.createBody({ type: 'static', position: [0, -0.05, lane] });
    world.addBox(floor, [9, 0.05, 1.25], { friction: surface.friction, restitution: surface.restitution, rollingResistance: surface.rollingResistance });
    if (scenario === 'wall-impact') {
      const wall = world.createBody({ type: 'static', position: [0, 1, lane] });
      world.addBox(wall, [0.02, 1, 1.2], { friction: 0.6, restitution: 0.1 });
    }
    const initial = scenarioInitialState(specs[id], id, scenario, lane);
    const ball = createSportsBall(world, specs[id], surface, initial.position);
    launchSportsBall(world, ball, initial.velocity, initial.spin);
    balls[id] = ball;
    buffer.add(ball.body);
  }
  buffer.readInto(world);
  return { world, balls, buffer, transforms: buffer.transforms, simulation: new SportsBallSimulation(world, IDS.map((id) => balls[id])) };
}

function scenarioInitialState(spec: SportsBallSpec, id: SportsBallId, scenario: ScenarioId, lane: number): { position: Vec3; velocity: Vec3; spin: Vec3 } {
  if (scenario === 'drop') return { position: [-2.5, spec.dropTest.heightM + spec.radiusM, lane], velocity: [0, 0, 0], spin: [0, 0, 0] };
  if (scenario === 'roll') return { position: [-5, spec.radiusM + 0.002, lane], velocity: [4, 0, 0], spin: [0, 0, -4 / spec.radiusM] };
  if (scenario === 'topspin' || scenario === 'backspin') {
    const speed = launchSpeed(id) * 0.35;
    const spinRatio = 0.24;
    const spin = spinRatio * speed / spec.radiusM * (scenario === 'topspin' ? -1 : 1);
    return { position: [-4.5, 1.15, lane], velocity: [speed, -4, 0], spin: [0, 0, spin] };
  }
  if (scenario === 'wall-impact') return { position: [-5, 1, lane], velocity: [launchSpeed(id), 0, 0], spin: [0, 0, 0] };
  const speed = launchSpeed(id) * 0.5;
  return { position: [-5, 0.7, lane], velocity: [speed, 5.5, 0], spin: [0, 0, 0] };
}

function updateMotion(bundle: SimulationBundle, specs: Record<SportsBallId, SportsBallSpec>, states: Record<SportsBallId, MotionState>): void {
  const events = bundle.world.drainContactBeginEvents();
  for (const id of IDS) {
    const index = IDS.indexOf(id) * 7;
    const state = states[id];
    const ball = bundle.balls[id];
    const velocity = bundle.world.getLinearVelocity(ball.body);
    const underside = bundle.transforms[index + 1] - specs[id].radiusM;
    if (events.some((event) => event.bodyA === ball.body || event.bodyB === ball.body)) state.contactSeen = true;
    if (!state.rising && state.previousY < -0.05 && velocity.y > 0.05 && underside < Math.max(0.12, specs[id].radiusM * 1.2)) {
      state.rising = true;
      state.apex = underside;
      state.exitSpeed = Math.hypot(velocity.x, velocity.z);
    }
    if (state.rising && state.apex !== null) state.apex = Math.max(state.apex, underside);
    state.previousY = velocity.y;
  }
}

function makeMetrics(bundle: SimulationBundle, specs: Record<SportsBallId, SportsBallSpec>, motion: Record<SportsBallId, MotionState>): Record<SportsBallId, LaneMetrics> {
  return Object.fromEntries(IDS.map((id, laneIndex) => {
    const index = laneIndex * 7;
    const velocity = bundle.world.getLinearVelocity(bundle.balls[id].body);
    const angular = bundle.world.getAngularVelocity(bundle.balls[id].body);
    const force = bundle.simulation.getLastForce(bundle.balls[id]) ?? emptyForce();
    const horizontalSpeed = Math.hypot(velocity.x, velocity.z);
    const rollingSpeed = specs[id].radiusM * Math.hypot(angular.x, angular.z);
    return [id, {
      ready: true,
      heightM: Math.max(0, bundle.transforms[index + 1] - specs[id].radiusM),
      speedMps: Math.hypot(velocity.x, velocity.y, velocity.z),
      spinRpm: Math.hypot(angular.x, angular.y, angular.z) * 60 / (2 * Math.PI),
      reynolds: force.reynolds,
      dragCoefficient: force.dragCoefficient,
      liftCoefficient: force.liftCoefficient,
      reboundApexM: motion[id].apex,
      slipMps: Math.abs(horizontalSpeed - rollingSpeed),
      contactSeen: motion[id].contactSeen,
      exitSpeedMps: motion[id].exitSpeed,
    } satisfies LaneMetrics];
  })) as unknown as Record<SportsBallId, LaneMetrics>;
}

function updateMesh(mesh: THREE.Mesh | null, transforms: Float32Array, index: number): void {
  if (!mesh) return;
  const offset = index * 7;
  mesh.position.set(transforms[offset], transforms[offset + 1], transforms[offset + 2]);
  mesh.quaternion.set(transforms[offset + 3], transforms[offset + 4], transforms[offset + 5], transforms[offset + 6]);
}

function createTrails(ghost: boolean): Record<SportsBallId, ReturnType<typeof createTrail>> {
  return Object.fromEntries(IDS.map((id) => [id, createTrail(ghost ? '#79eedb' : SPORTS_BALL_PRESETS[id].visual.color, ghost)])) as Record<SportsBallId, ReturnType<typeof createTrail>>;
}

function createTrail(color: string, ghost: boolean) {
  const capacity = 220;
  const values = new Float32Array(capacity * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(values, 3));
  geometry.setDrawRange(0, 0);
  const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: ghost ? 0.28 : 0.72 });
  return { capacity, values, count: 0, geometry, line: new THREE.Line(geometry, material) };
}

function pushTrail(trail: ReturnType<typeof createTrail>, point: THREE.Vector3): void {
  if (trail.count < trail.capacity) {
    const index = trail.count * 3;
    trail.values.set([point.x, point.y, point.z], index);
    trail.count += 1;
  } else {
    trail.values.copyWithin(0, 3);
    trail.values.set([point.x, point.y, point.z], (trail.capacity - 1) * 3);
  }
  const attribute = trail.geometry.getAttribute('position') as THREE.BufferAttribute;
  attribute.needsUpdate = true;
  trail.geometry.setDrawRange(0, trail.count);
}

function destroyPair(pair: ComparisonPair): void { pair.live.world.destroy(); pair.reference.world.destroy(); }
function emptyMetrics(): Record<SportsBallId, LaneMetrics> { return { football: { ...EMPTY_METRIC }, basketball: { ...EMPTY_METRIC }, tennis: { ...EMPTY_METRIC } }; }
function emptyMotion(): Record<SportsBallId, MotionState> { return Object.fromEntries(IDS.map((id) => [id, { previousY: 0, rising: false, apex: null, contactSeen: false, exitSpeed: null }])) as Record<SportsBallId, MotionState>; }
function emptyForce(): BallForceSample { return { relativeVelocity: [0, 0, 0], reynolds: 0, spinRatio: 0, dragCoefficient: 0, liftCoefficient: 0, dragForce: [0, 0, 0], magnusForce: [0, 0, 0], totalForce: [0, 0, 0] }; }

function defaultConfig(id: SportsBallId): LaneConfig { const spec = SPORTS_BALL_PRESETS[id]; return { surfaceId: defaultSurface(id), massKg: spec.massKg, restitutionScale: 1, dragScale: 1, liftScale: 1 }; }
function defaultSurface(id: SportsBallId): BallSurfaceId { return id === 'basketball' ? 'hardwood' : id === 'tennis' ? 'hard-court' : 'grass'; }
function launchSpeed(id: SportsBallId): number { return id === 'football' ? 35 : id === 'basketball' ? 20 : 50; }
function shortLabel(id: SportsBallId): string { return id === 'football' ? 'Football' : id === 'basketball' ? 'Basketball' : 'Tennis'; }

function resolveSpec(id: SportsBallId, config: LaneConfig): SportsBallSpec {
  const base = SPORTS_BALL_PRESETS[id];
  return resolveSportsBallSpec(id, {
    massKg: config.massKg,
    material: { restitutionByImpactSpeed: base.material.restitutionByImpactSpeed.map((point) => ({ ...point, y: Math.min(1, point.y * config.restitutionScale) })) },
    aero: {
      dragByReynolds: base.aero.dragByReynolds.map((point) => ({ ...point, y: point.y * config.dragScale })),
      liftBySpinRatio: base.aero.liftBySpinRatio.map((point) => ({ ...point, y: point.y * config.liftScale })),
    },
  });
}

function buildProfile(id: SportsBallId, config: LaneConfig): SportsBallProfile {
  const base = SPORTS_BALL_PRESETS[id];
  const reference = config.surfaceId === defaultSurface(id) && config.massKg === base.massKg && config.restitutionScale === 1 && config.dragScale === 1 && config.liftScale === 1;
  return createSportsBallProfile({
    name: `${base.label} ${reference ? 'reference' : 'game override'}`,
    mode: reference ? 'reference' : 'game',
    presetId: id,
    surfaceId: config.surfaceId,
    overrides: reference ? {} : {
      ...(config.massKg !== base.massKg ? { massKg: config.massKg } : {}),
      ...(config.restitutionScale !== 1 ? { material: { restitutionByImpactSpeed: base.material.restitutionByImpactSpeed.map((point) => ({ ...point, y: Math.min(1, point.y * config.restitutionScale) })) } } : {}),
      ...(config.dragScale !== 1 || config.liftScale !== 1 ? { aero: {
        ...(config.dragScale !== 1 ? { dragByReynolds: base.aero.dragByReynolds.map((point) => ({ ...point, y: point.y * config.dragScale })) } : {}),
        ...(config.liftScale !== 1 ? { liftBySpinRatio: base.aero.liftBySpinRatio.map((point) => ({ ...point, y: point.y * config.liftScale })) } : {}),
      } } : {}),
    },
  });
}

function scenarioOutcome(scenario: ScenarioId, metric: LaneMetrics): string {
  if (scenario === 'drop') return metric.reboundApexM === null ? '--' : `${metric.reboundApexM.toFixed(3)} m`;
  if (scenario === 'roll') return `${metric.slipMps.toFixed(2)} m/s slip`;
  if (scenario === 'wall-impact') return metric.contactSeen ? 'CONTACT / HELD' : 'approaching';
  if (scenario === 'topspin' || scenario === 'backspin') return metric.exitSpeedMps === null ? '--' : `${metric.exitSpeedMps.toFixed(2)} m/s exit`;
  return `${metric.speedMps.toFixed(2)} m/s retained`;
}

function formatCompact(value: number): string { if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`; if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`; return value.toFixed(0); }

function ControlSection({ title, children }: { title: string; children: ReactNode }) { return <section className="ball-v2-control"><h2>{title}</h2>{children}</section>; }

function Slider({ testId, label, value, min, max, step, suffix, onChange }: { testId: string; label: string; value: number; min: number; max: number; step: number; suffix: string; onChange: (value: number) => void }) {
  return <label className="ball-v2-slider"><span>{label}<strong>{value.toFixed(step < 0.01 ? 3 : 2)}{suffix}</strong></span><input data-testid={testId} type="range" value={value} min={min} max={max} step={step} onChange={(event) => onChange(Number(event.target.value))} /></label>;
}
